import React, { useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { DeleteChildren } from '../../../../../../connection/products';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { ColorKeys, ContentEditors, FeatureFlagsEnum, IProductAttribute, IProductAttributeLabel, Product, TextOverrides } from '../../../../../../types/working-model';
import SortableProduct from './product';
import CreateProductModal from '../../../../content-creation/products';
import { getDefaultModuleAlignment, getTextValue, toDict, updateTranslateKey } from '../../../../../../utils/utils';
import { ProductsLayoutTypesBold, ProductsLayoutTypesClassic, ProductsLayoutTypesFun, TemplateClassNames } from '../../../../../../types/template-layouts';
import Controls from '../../../session/editor/moduleControls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';
import '../../../../../../scss/live-event/base/modules/products.scss';
import './products.scss';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import LargeButton from '../../../../../general-ui/button/large-button';
import { BasicLangingModuleProps } from '../editor-main';
import FilterDropdown from '../../../../../live-event/modules/agenda/session-filter-dropdown';
import { Button } from '../../../../../general-ui/button/button';
import { EIcon } from '../../../../../general-ui/icon/icon';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import DeleteProductConfirmation from '../../../../content-creation/delete-product-confirmation';
import { handleCompleteSelection } from './products-utils';

const EditorProducts: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>((
	{
		page_module,
		template,
		language,
		baseLanguage,
	},
	ref
) => {
	const dispatch = useDispatch();

	const contentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const eventId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.event);
	const channelId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.channel);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const useEventHierarchy = featureFlags[FeatureFlagsEnum.event_hierarchy];

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	const [modalOpen, setModalOpen] = useState(false);
	const [productToEdit, setProductToEdit] = useState<null | Product>(null);
	const [replacing, setReplacing] = useState<Product | null>(null);
	const [selectedAttribute, setSelectedAttribute] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('');
	const [showMore, setShowMore] = useState(false);
	const [confirmModal, setConfirmModal] = useState(false);
	const [removing, setRemoving] = useState<Product | undefined>();
	const [saving, setSaving] = useState(false);
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);

	const content = page_module?.content;
	const layout_type = page_module?.content?.layout_type || "standard";
	const styling_overrides = page_module?.styling_overrides;

	if (!page_module?.modules?.[0]) { //safety check for null values in the modules array
		page_module.modules = [];
	}

	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);

	function handleShowMore() {
		setShowMore(prev => !prev);
	}

	const maxProducts = useMemo(() => {
		switch (layout_type) {
			case ProductsLayoutTypesClassic.standard:
			case ProductsLayoutTypesBold.standard:
			case ProductsLayoutTypesFun.standard:
				return 3;

			case ProductsLayoutTypesClassic.grid:
			case ProductsLayoutTypesBold.grid:
			case ProductsLayoutTypesFun.grid:
				return 4;

			case ProductsLayoutTypesClassic.vertical_list:
			case ProductsLayoutTypesBold.vertical_list:
			case ProductsLayoutTypesFun.vertical_list:
				return 4;

			default:
				return 2;
		}
	}, [layout_type]);

	const limitProductsIndex = useCallback(() => {
		if (showMore) return page_module?.modules?.length ?? maxProducts;
		else return maxProducts;
	}, [showMore, page_module?.modules?.length, maxProducts]);

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							language,
							baseLanguage
						}),
					},
					...(key === 'description' && { is_edited: true }),
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const displayEditModal = useCallback((product: Product) => {
		setProductToEdit(product);
		setModalOpen(true);
	}, []);

	const replaceProduct = useCallback((product: Product) => {
		setProductToEdit(null);
		setReplacing(product);
		setModalOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setModalOpen(false);
		setReplacing(null);
	}, []);

	const openModal = useCallback(() => {
		setReplacing(null);
		setProductToEdit(null);
		setModalOpen(true);
	}, []);

	function handleDragEnd(event: any) {
		const _modules = [...(page_module.modules || [])];

		const startIndex = _modules.findIndex((product: Product) => Number(product.product) === Number(event.active.id));
		const newIndex = _modules.findIndex((product: Product) => Number(product.product) === Number(event.over.id));

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updateHomepageModule(
				{
					...page_module,
					modules: _modules,
					content_modules: _modules.map((product: Product) => product.product)
				}
			)
		);
	}

	function onSelectionComplete(products: Product[]) {
		handleCompleteSelection({
			token,
			channelId,
			eventId,
			page_module,
			setSaving,
			products,
			dispatch
		});
	}

	function confirmRemoveProduct(product: Product) {
		if (useEventHierarchy && product.event_id) {
			setRemoving(product);
			setConfirmModal(true);
		} else {
			removeProduct(product);
		}
	}

	function closeConfirmation() {
		setConfirmModal(false);
		setRemoving(undefined);
	}

	async function removeProduct(productToRemove?: Product) {
		if (!token || !productToRemove?.module_id) return;
		if (page_module.modules) {
			const filteredModules = page_module.modules.filter((module: Product) => module.product !== productToRemove.product);
			dispatch(
				updateHomepageModule({
					...page_module,
					modules: filteredModules,
					content_modules: filteredModules.map(product => product.product)
				})
			);

			// if using event hierarchy AND product contains an event id, meaning it is a child, follow delete child route
			// running this after dispatch, because possible failure on the delete shouldn't prohibit removing the product
			if (useEventHierarchy && productToRemove.event_id) {
				try {
					await DeleteChildren(token, [productToRemove.module_id], productToRemove.event_id);
				} catch (e) {
					console.error(e);
				}
			}
		}
	}

	const productAttributes = useMemo(() => {
		if (!page_module?.modules?.length) return [];
		const modules = page_module?.modules;
		let allAttributes: { label: string, value: string }[] = [];
		const existsMap: { [key: string]: any } = {};
		modules?.forEach((module: Product) => {
			if (module?.attributes?.length) {
				module.attributes.forEach((attribute: IProductAttribute, index: number) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (key && value) {
						if (!existsMap?.[key]) {
							existsMap[key] = {};
						}
						if (!existsMap[key]?.[value]) {
							existsMap[key][value] = value;
							const label = t(`${translationRoute}:product.${module.module_id}.attribute.${index}.type`, key) + " / " + t(`${translationRoute}:product.${module.module_id}.attribute.${index}.value`, value);
							allAttributes.push({
								label: label,
								value: `${key} / ${value}`,
							});
						}
					}
				});
			}
		});
		allAttributes = allAttributes.sort((a: IProductAttributeLabel, b: IProductAttributeLabel) => {
			return a.label > b.label ? 1 : -1;
		});

		return allAttributes;
	}, [page_module?.modules, t, translationRoute]);

	const productCategories = useMemo(() => {
		if (!page_module?.modules?.length) return [];
		const modules = page_module?.modules;

		const allCategories: string[] = [];
		modules?.forEach((module: Product) => {
			if (module?.categories?.length) {
				allCategories.push(...module.categories);
			}
		});
		// convert to set and then back to array to ensure all strings are unique
		const uniqueCategories = Array.from(new Set(allCategories)).map((category: string) => ({
			label: category,
			value: category,
		}));
		uniqueCategories.sort((a: IProductAttributeLabel, b: IProductAttributeLabel) => {
			return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
		});
		return uniqueCategories;
	}, [page_module?.modules]);

	const { custom_heading, description, title } = getTextValue(content, language);

	const visibleProducts = useMemo(() => {
		if (!page_module?.modules?.length) return [];
		let itemsToRender = [...(page_module?.modules ?? [])];
		if (selectedCategory || selectedAttribute) {
			itemsToRender = itemsToRender.filter((module: Product) => {

				// if there's a selected category, then find it, otherwise set it to true
				const matchedCategory = selectedCategory ? (module?.categories ?? []).find(category => {
					return category?.toLowerCase().includes(selectedCategory.toLowerCase());
				}) : true;

				// if there's a selected attribute, then find it, otherwise set it to true
				const matchedAttribute = selectedAttribute ? (module?.attributes ?? []).find((attribute: IProductAttribute) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (`${key} / ${value}` === selectedAttribute) return true;
					return false;
				}) : true;

				// if both both category and attribute are true, then return the product
				return matchedCategory && matchedAttribute;
			});
		}
		return itemsToRender;
	}, [page_module?.modules, selectedAttribute, selectedCategory]);

	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	return page_module.is_on ? (
		<section
			ref={ref}
			className={classNames(
				"editor-wrapper products",
				`products-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				openContentModal={openModal}
				module={page_module}
				contentButtonText="Add Products"
				editableContent={{
					jsx: <p>placeholder</p>,
					onDone: () => ('placeholder')
				}}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="products-text-wrapper">
					<EditableTextSlim
						tagType="h5"
						className="products-custom-heading evt-caption-1"
						value={t(`${translationRoute}:products.${page_module.id}.heading`, t(`${translationRoute}:products.heading`, custom_heading))}
						defaultColor={customHeadingDefaultColor}
						onChange={handleTextChange('custom_heading')}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
						elementName={EContentColorOverrideNames.custom_heading}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
					/>

					<div className="products-top">
						<div className="text-container">
							<EditableTextSlim
								tagType="h2"
								className="products-title evt-heading-2"
								value={t(`${translationRoute}:products.${page_module.id}.title`, t(`${translationRoute}:products.title`, title))}
								defaultColor={ColorKeys.bodyTextColor}
								onChange={handleTextChange('title')}
								onOverrideChange={handleStyleOverrideChange('title')}
								elementName={EContentColorOverrideNames.title}
								colorOverride={styling_overrides?.content?.title?.color}
							/>
							<EditableTextDraftQuill
								className="products-description evt-body-text-1"
								value={t(`${translationRoute}:products.${page_module.id}.description`, t(`${translationRoute}:products.description`, description))}
								onChange={handleTextChange('description')}
								defaultColor={ColorKeys.bodyTextColor}
								onOverrideChange={handleStyleOverrideChange('description')}
								elementName={EContentColorOverrideNames.description}
								styleOverrides={styling_overrides?.content?.description}
								useNew={contentEditor === ContentEditors.quill}
								defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
							/>
						</div>
						<div className="dropdown-container">
							<FilterDropdown
								options={[{ label: t('homepage:All attributes'), value: '' }, ...productAttributes]}
								onChange={(option) => setSelectedAttribute(option.value.toString())}
								template={template || TemplateClassNames.Apollo}
								value={selectedAttribute}
							/>
							<FilterDropdown
								options={[{ label: t('homepage:All categories'), value: '' }, ...productCategories]}
								onChange={(option) => setSelectedCategory(option.value.toString())}
								template={template || TemplateClassNames.Apollo}
								value={selectedCategory}
							/>
						</div>
					</div>
				</div>
				{(page_module.modules?.length) //safety check for null product value
					? (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={page_module?.modules?.map?.((module: Product) => String(module.product))}
								strategy={rectSortingStrategy}
							>
								<ul className="product-list">
									{visibleProducts?.slice(0, limitProductsIndex()).map((module: Product) => (
										<SortableProduct
											product={module}
											key={module.product}
											id={String(module.product)}
											editProduct={() => displayEditModal(module)}
											removeProduct={() => confirmRemoveProduct(module)}
											replaceProduct={() => replaceProduct(module)}
											template={template}
											language={language}
										/>
									))}
								</ul>
							</SortableContext>
						</DndContext>
					)
					: <LargeButton
						title="Add Product"
						onClick={openModal}
						style={{ width: "100%", marginTop: '20px' }}
						nonUpload
					/>}
				{visibleProducts?.length > maxProducts ? (
					<Button
						classButton={"secondary products-more-button"}
						template={template}
						onClick={handleShowMore}
					>
						{template == TemplateClassNames.Apollo && (
							<EIcon
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
						)}
						{t("homepage:Show")} {showMore ? t('homepage:Less') : t('homepage:More')}
					</Button>
				) : null}
			</div>

			<CreateProductModal
				baseLanguage={baseLanguage}
				language={language}
				title={"Add products"}
				open={modalOpen}
				productToEdit={productToEdit}
				close={closeModal}
				onComplete={onSelectionComplete}
				initialSelected={page_module.modules}
				replacing={replacing}
			/>
			<DeleteProductConfirmation
				onConfirm={() => removeProduct(removing)}
				onClose={() => closeConfirmation()}
				open={confirmModal}
				product={removing}
			/>
		</section>
	) : null;
});

EditorProducts.displayName = 'EditorProducts';

export default EditorProducts;